import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DataTable from "../../Table/DataTable";
import VerticalPoints from "../../Common/VerticalPoints/VerticalPoints";
import API from "../../../Api/Api";
import { API_END_POINT } from "../../../Api/EndPoints";
import ConfirmModal from "../../Common/Modal/ConfirmModal";
import { openRightModal } from "../../../REDUX/actions/rightModal";
import { openCenteredModal } from "../../../REDUX/actions/centeredModal";
import {
  deleteCompetitor,
  fetchCompetitors,
} from "../../../REDUX/actions/competitors";
import CompetitorModal from "./CompetitorModal";
import { competitorForm } from "./data";

const formOptions = (name) =>
  competitorForm.find((field) => field.name === name)?.options ?? [];

const filterElements = [
  {
    type: "select",
    label: "Competitor type",
    name: "competitorType",
    placeholder: "Competitor type",
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Pool/Spa Group",
    name: "poolSpaGroup",
    placeholder: "Pool/Spa Group",
    filter: true,
    resetable: true,
    showClear: true,
  },
  {
    type: "select",
    label: "Brand",
    name: "spaMarque",
    placeholder: "Brand",
    filter: true,
    resetable: true,
    showClear: true,
  },
];

// Competitor list — Strapi envelope rows (row.attributes.*), scoped
// server-side (reseller = own organization; admin = all).
const CompetitorsTable = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { competitors, loading, pageCount, total, deleteLoading } = useSelector(
    (state) => state.competitorsReducer
  );

  const [filters, setFilters] = useState();
  const [spaMarques, setSpaMarques] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    API.get(`${API_END_POINT}api/spa-marques`, { signal: controller.signal })
      .then(({ data }) =>
        setSpaMarques(
          (data?.data ?? []).map((marque) => ({
            name: marque.attributes?.name,
            code: marque.id,
          }))
        )
      )
      .catch((error) => {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      });
    return () => controller.abort();
  }, []);

  const selectOptions = {
    competitorType: formOptions("competitorType").map((option) => ({
      ...option,
      name: t(option.name),
    })),
    poolSpaGroup: formOptions("poolSpaGroup"),
    spaMarque: spaMarques,
  };

  const fetchData = (page, search, tableFilters, signal) => {
    dispatch(fetchCompetitors(search, page, signal, tableFilters));
  };

  const headers = [
    "Company name",
    "Phone number",
    "Email",
    "Pool/Spa Group",
    "Competitor type",
    "",
  ];
  const columns = [
    {
      name: "Company name",
      render: (row) => row?.attributes?.companyName ?? "-",
    },
    {
      name: "Phone number",
      render: (row) => row?.attributes?.phoneNumber ?? "-",
    },
    { name: "Email", render: (row) => row?.attributes?.email ?? "-" },
    {
      name: "Pool/Spa Group",
      render: (row) => row?.attributes?.poolSpaGroup ?? "-",
    },
    {
      name: "Competitor type",
      render: (row) =>
        row?.attributes?.competitorType
          ? t(
              row.attributes.competitorType === "direct" ? "Direct" : "Indirect"
            )
          : "-",
    },
    {
      name: "options",
      render: (row) => (
        <VerticalPoints
          showLeft
          options={[
            {
              text: t("Edit"),
              callback: () =>
                dispatch(openRightModal(<CompetitorModal competitor={row} />)),
            },
            {
              text: t("Delete"),
              red: true,
              loading: deleteLoading,
              callback: () =>
                dispatch(
                  openCenteredModal(
                    <ConfirmModal
                      label={row?.attributes?.companyName ?? ""}
                      id={row?.id}
                      onConfirm={() => dispatch(deleteCompetitor(row.id, t))}
                    />
                  )
                ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      title="General"
      total={total}
      headers={headers}
      columns={columns}
      data={competitors}
      loading={loading}
      totalPages={pageCount}
      fetchData={fetchData}
      filters={filters}
      setFilters={setFilters}
      filterElements={filterElements}
      selectOptions={selectOptions}
      addNewItem={() => dispatch(openRightModal(<CompetitorModal />))}
      newItemTitle={t("Add a competitor")}
      noResultsMessage={t("No competitors found.")}
      showRowDetails={false}
    />
  );
};

export default CompetitorsTable;
